import { z } from "zod";
import { environmentSchema } from "./context.js";
const identifier = z.string().regex(/^[A-Za-z0-9_-]{1,200}$/);
const timestamp = z.string().datetime({ offset: true });
const digest = z.string().regex(/^[a-f0-9]{64}$/);
/** Commercial metadata only. This is not the legal-source registry or fiscal evidence. */
export const commercialDriveWatchConfigSchema = z
    .object({ enabled: z.boolean(), folderId: identifier })
    .strict();
/** Provider versions/checksums describe metadata; none is a downloaded content hash. */
export const commercialDriveFileMetadataSchema = z
    .object({
    fileId: identifier,
    mimeType: z.string().min(1).max(200),
    version: z.string().regex(/^\d{1,30}$/),
    headRevisionId: identifier.nullish(),
    md5Checksum: z
        .string()
        .regex(/^[a-fA-F0-9]{32}$/)
        .nullish(),
    modifiedTime: timestamp.nullish(),
    size: z
        .string()
        .regex(/^\d{1,30}$/)
        .nullish(),
})
    .strict();
export const commercialDriveWatchErrorCodeSchema = z.enum([
    "AUTHENTICATION_FAILED",
    "ACCESS_DENIED",
    "NOT_FOUND",
    "API_DISABLED",
    "RATE_LIMITED",
    "NETWORK_ERROR",
    "TIMEOUT",
    "INVALID_RESPONSE",
    "PAGINATION_CYCLE",
    "PAGE_TOKEN_REJECTED",
    "CAPACITY_EXCEEDED",
    "INCOMPLETE_SEARCH",
    "LEASE_LOST",
    "BUDGET_EXCEEDED",
    "INTERNAL_ERROR",
]);
export const commercialDriveWatchRunKindSchema = z.enum(["DAILY_CHECK", "MONTHLY_RECONCILIATION"]);
export const commercialDriveWatchRunStatusSchema = z.enum([
    "RUNNING",
    "RETRY_PENDING",
    "PAUSED",
    "COMPLETED",
    "FAILED",
    "SUPERSEDED",
]);
export const commercialDriveWatchRunSchema = z.object({
    id: z.string().uuid(),
    watchId: z.string().uuid(),
    kind: commercialDriveWatchRunKindSchema,
    status: commercialDriveWatchRunStatusSchema,
    configVersion: digest,
    attempt: z.number().int().min(1).max(3),
    pageCount: z.number().int().nonnegative(),
    observedCount: z.number().int().nonnegative(),
    newlyObservedCount: z.number().int().nonnegative(),
    metadataChangedCount: z.number().int().nonnegative(),
    notObservedCount: z.number().int().nonnegative(),
    startedAt: timestamp,
    finishedAt: timestamp.nullable(),
    retryAt: timestamp.nullable(),
    errorCode: commercialDriveWatchErrorCodeSchema.nullable(),
    historicalCompleteness: z.literal("UNKNOWN"),
    executable: z.literal(false),
});
export const commercialDriveWatchSchema = z.object({
    id: z.string().uuid(),
    organizationId: z.string().uuid(),
    environment: environmentSchema,
    enabled: z.boolean(),
    folderId: identifier,
    scope: z.literal("DIRECT_CHILDREN"),
    configVersion: digest,
    nextCheckAt: timestamp,
    nextReconciliationAt: timestamp,
    lastCompletedAt: timestamp.nullable(),
    historicalCompleteness: z.literal("UNKNOWN"),
    executable: z.literal(false),
});
export const commercialDriveWatchStatusSchema = z.object({
    schemaVersion: z.literal("1.0.0"),
    watch: commercialDriveWatchSchema.nullable(),
    lastRun: commercialDriveWatchRunSchema.nullable(),
    lastCompletedRun: commercialDriveWatchRunSchema.nullable(),
    historicalCompleteness: z.literal("UNKNOWN"),
    executable: z.literal(false),
});
export const commercialDriveFileObservationSchema = commercialDriveFileMetadataSchema.extend({
    runId: z.string().uuid(),
    observedAt: timestamp,
    metadataDigest: digest,
    change: z.enum(["NEWLY_OBSERVED", "METADATA_CHANGED", "UNCHANGED", "NOT_OBSERVED"]),
    contentStatus: z.literal("NOT_DOWNLOADED"),
    sourceContentSha256: z.null(),
    executable: z.literal(false),
});
export const commercialDriveWatchFilesSchema = z.object({
    schemaVersion: z.literal("1.0.0"),
    run: commercialDriveWatchRunSchema.nullable(),
    files: z.array(commercialDriveFileObservationSchema).max(100),
    nextAfterFileId: identifier.nullable(),
    historicalCompleteness: z.literal("UNKNOWN"),
    executable: z.literal(false),
});
/** Dedicated operational receipt; never fiscal/source-content acceptance. Legacy v1 is unchanged. */
export const commercialDriveWatchScheduledReceiptSchema = z
    .object({
    event: z.literal("commercial_drive_watch_scheduled"),
    protocol: z.literal("commercial-drive-watch-scheduled.v1"),
    purpose: z.literal("METADATA_ONLY"),
    environment: z.literal("dev"),
    execution: z.string().regex(/^bc-tax-dev-commercial-drive-watch-[a-z0-9-]{1,80}$/),
    correlationId: z.string(),
    organizationSha256: digest,
    folderSha256: digest,
    configVersion: digest,
    runId: z.string().uuid().nullable(),
    tickStatus: z.enum(["NOT_DUE", "COMPLETED", "PAUSED", "FAILED", "LEASE_LOST"]),
    runKind: commercialDriveWatchRunKindSchema.nullable(),
    newlyObservedCount: z.number().int().min(0).max(2000),
    metadataChangedCount: z.number().int().min(0).max(2000),
    notObservedCount: z.number().int().min(0).max(2000),
    status: z.enum(["SUCCESSFUL", "NOT_DUE", "PAUSED", "BLOCKED"]),
    pageCount: z.number().int().min(0).max(20),
    observedCount: z.number().int().min(0).max(2000),
    finishedAt: timestamp.nullable(),
    observedAt: timestamp,
    historicalCompleteness: z.literal("UNKNOWN"),
    contentStatus: z.literal("NOT_DOWNLOADED"),
    sourceContentSha256: z.null(),
    researchExecuted: z.literal(false),
    contentIngestionExecuted: z.literal(false),
    executable: z.literal(false),
})
    .strict()
    .superRefine((value, context) => {
    if (value.correlationId !== value.execution ||
        (value.status === "SUCCESSFUL" &&
            (value.tickStatus !== "COMPLETED" ||
                value.runId === null ||
                value.runKind === null ||
                value.newlyObservedCount + value.metadataChangedCount > value.observedCount ||
                value.pageCount < 1 ||
                value.finishedAt === null ||
                Date.parse(value.finishedAt) > Date.parse(value.observedAt))) ||
        (value.status !== "SUCCESSFUL" &&
            (value.pageCount !== 0 ||
                value.observedCount !== 0 ||
                value.finishedAt !== null ||
                value.runKind !== null ||
                value.newlyObservedCount !== 0 ||
                value.metadataChangedCount !== 0 ||
                value.notObservedCount !== 0)) ||
        (value.status === "NOT_DUE" && (value.tickStatus !== "NOT_DUE" || value.runId !== null)) ||
        (value.status === "PAUSED" && value.tickStatus !== "PAUSED"))
        context.addIssue({ code: "custom", message: "Metadata execution receipt is incoherent" });
});
//# sourceMappingURL=commercial-drive-watch.js.map